import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
export const MIN_SIDEBAR_WIDTH = 200;
export const MAX_SIDEBAR_WIDTH = 480;

/**
 * Key/default are exported so the loading skeleton (SidebarSkeleton) can
 * render at the width the real sidebar will restore.
 */
export const SESSION_SIDEBAR_WIDTH_KEY = "assistantSidebarWidth";
export const DEFAULT_SESSION_SIDEBAR_WIDTH = 330;
export const REDESIGN_SIDEBAR_WIDTH_KEY = "codesmithSidebarRedesignWidth";
export const REDESIGN_MIN_SIDEBAR_WIDTH = 215;
export const DEFAULT_REDESIGN_SIDEBAR_WIDTH = 215;
export interface SidebarWidthConfig {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}
export function sidebarWidthConfig(variant: CodesmithSidebarVariant): SidebarWidthConfig {
  return variant === "redesign" ? {
    storageKey: REDESIGN_SIDEBAR_WIDTH_KEY,
    defaultWidth: DEFAULT_REDESIGN_SIDEBAR_WIDTH,
    minWidth: REDESIGN_MIN_SIDEBAR_WIDTH,
    maxWidth: MAX_SIDEBAR_WIDTH
  } : {
    storageKey: SESSION_SIDEBAR_WIDTH_KEY,
    defaultWidth: DEFAULT_SESSION_SIDEBAR_WIDTH,
    minWidth: MIN_SIDEBAR_WIDTH,
    maxWidth: MAX_SIDEBAR_WIDTH
  };
}
export function clampSidebarWidth(width: number, {
  minWidth,
  maxWidth
}: SidebarWidthConfig): number {
  return Math.min(maxWidth, Math.max(minWidth, width));
}
export function useResizableSidebar(t0) {
  const $ = _c(10);
  const variant = t0 === undefined ? "legacy" : t0;
  const config = sidebarWidthConfig(variant);
  const [storedWidth, setSidebarWidth] = useLocalStorage(config.storageKey, config.defaultWidth);
  const sidebarWidth = clampSidebarWidth(storedWidth, config);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      e.preventDefault();
      setIsResizing(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleResizeMouseDown = t1;
  const {
    minWidth,
    maxWidth
  } = config;
  let t2;
  let t3;
  if ($[1] !== isResizing || $[2] !== maxWidth || $[3] !== minWidth || $[4] !== setSidebarWidth) {
    t2 = () => {
      if (!isResizing) {
        return;
      }
      let rafId = null;
      let pendingWidth = null;
      const handleMouseMove = e_0 => {
        if (!sidebarRef.current) {
          return;
        }
        const containerRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = e_0.clientX - containerRect.left;
        pendingWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        if (rafId === null) {
          rafId = requestAnimationFrame(() => {
            if (pendingWidth !== null) {
              setSidebarWidth(pendingWidth);
            }
            rafId = null;
          });
        }
      };
      const handleMouseUp = () => {
        if (pendingWidth !== null) {
          setSidebarWidth(pendingWidth);
        }
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        setIsResizing(false);
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t3 = [isResizing, setSidebarWidth, minWidth, maxWidth];
    $[1] = isResizing;
    $[2] = maxWidth;
    $[3] = minWidth;
    $[4] = setSidebarWidth;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== isResizing || $[8] !== sidebarWidth) {
    t4 = {
      sidebarWidth,
      isResizing,
      sidebarRef,
      handleResizeMouseDown
    };
    $[7] = isResizing;
    $[8] = sidebarWidth;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}